import { useState, useEffect, useContext } from "react";
import Preloader from "../../shared/PreLoaders";
import { AuthContext } from "../../Context/AuthContext";
import { logActivity } from "../../utils/LogActivity";
import TableComponent from "../../utils/TableComponent";
import { useNavigate } from "react-router-dom";
import { Authorization } from "../../Context/Authorization";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const ManageLoan = () => {
  const [loans, setLoans] = useState([]);
  const [error, setError] = useState("");
  const [vfcId, setVfcId] = useState("");
  const [loanType, setLoanType] = useState("");
  const [loanNo, setLoanNo] = useState("");
  const [date, setDate] = useState("");
  const [vfcOptions, setVfcOptions] = useState([]);
  const [loanTypeOptions, setLoanTypeOptions] = useState([]);
  const [hasFiltered, setHasFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userId, permissions, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;
  const [manageData, setManageData] = useState([]);
  const navigate = useNavigate();

  const fetchVfcNames = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, config);
      const data = response.data.vfcs;
      if (Array.isArray(data)) setVfcOptions(data);
      else console.error("API did not return an array", data);
    } catch (error) {
      console.log("Error fetching Data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcNames(selectedJfmcType._id);
  }, [selectedJfmcType]);

  useEffect(() => {
    setLoading(true);
    const fetchLoanTypes = async () => {
      try {
        const config = Authorization();
        const response = await fetch(`${Api}/api/loan-types`, config);
        const data = await response.json();
        if (Array.isArray(data.loanTypes)) setLoanTypeOptions(data.loanTypes);
        else setLoanTypeOptions(data.loanTypes);
      } catch (error) {
        console.log("Error fetching loan types", error);
      }
      setLoading(false);
    };
    fetchLoanTypes();
  }, [Api]);

  const handleFilter = async () => {
    setLoading(true);
    setError("");
    setLoans([]);
    setHasFiltered(false);
    const filterData = {};
    if (vfcId) filterData.vfcId = vfcId;
    if (loanType) filterData.loanType = loanType;
    if (loanNo) filterData.loanNo = loanNo;
    if (date) filterData.date = date;
    try {
      const Config = Authorization();
      const response = await fetch(`${Api}/api/loans/filter`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...Config.headers },
        body: JSON.stringify(filterData),
      });
      const data = await response.json();
      setHasFiltered(true);
      if (data.data && data.data.length > 0) { setLoans(data.data); setError(""); }
      else { setLoans([]); setError("No Loans On the selected Field"); }
    } catch (err) {
      console.error("API Error:", err);
      setError("Something went wrong while filtering");
      setLoans([]);
    } finally {
      setLoading(false);
    }
    await logActivity(userId, "Viewed Loans By Filter", "Manage Loan Page", Api);
  };

  const handleDeleteState = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Loan?")) return;
    try {
      const config = Authorization();
      await axios.delete(`${Api}/api/loans/${id}`, config);
      setManageData(manageData.filter((state) => state._id !== id));
      await logActivity(userId, "Deleted Loan", "Loan Page", Api);
      toast.success("Deleted Successfully");
    } catch (err) {
      console.error("Error deleting Loan", err);
      toast.error("Failed to delete Loan");
    }
  };

  const columns = [
    {
      headerName: "Action", field: "action",
      cellRenderer: (params) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 4 }}>
          {permissions.includes("EditVfc") && (
            <i className="mdi mdi-pen" title="Edit"
              style={{ fontSize: "1.2rem", cursor: "pointer", color: "#1565C0" }}
              onClick={() => { if (window.confirm("Are you sure you want to edit this loan?")) navigate(`/edit-loan/${params.data._id}`); }}
            />
          )}
          {permissions.includes("DeleteLoan") && (
            <i className="mdi mdi-delete" title="Delete"
              style={{ fontSize: "1.2rem", cursor: "pointer", color: "#C62828" }}
              onClick={() => handleDeleteState(params.data._id)}
            />
          )}
        </div>
      ),
    },
    { headerName: "SI.No", field: "index", valueGetter: (p) => p.node.rowIndex + 1 },
    { headerName: "Name", field: "subLoans", valueGetter: (p) => p.data.subLoans?.[0]?.memberId?.name || "N/A" },
    { headerName: "VFC ID", field: "vfcId.vfcName" },
    { headerName: "Loan No", field: "loanNo" },
    { headerName: "Date", field: "date", valueFormatter: (p) => new Date(p.value).toLocaleDateString() },
    { headerName: "Total Loan Amount", field: "amount" },
    { headerName: "Loan Type", field: "loanType.loanName" },
    { headerName: "Interest Paid", field: "totalInterestPaid" },
    { headerName: "Total Principal Paid", field: "totalPrincipalPaid" },
    { headerName: "Balance Amount", field: "totalLoanBalance" },
  ];

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Manage Loans</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Loan</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Manage Loans</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Loans</span>
              <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Apply filters to search loans</span>
            </div>

            {error && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-alert-circle-outline" /> {error}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>JFMC</label>
                <Select
                  placeholder="All JFMC's"
                  value={vfcOptions.map((v) => ({ value: v._id, label: v.vfcName })).find((opt) => opt.value === vfcId) || null}
                  onChange={(selected) => setVfcId(selected ? selected.value : "")}
                  options={vfcOptions.map((v) => ({ value: v._id, label: v.vfcName }))}
                  isClearable isSearchable
                />
              </div>
              <div>
                <label style={labelStyle}>Loan Type</label>
                <select value={loanType} onChange={(e) => setLoanType(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">All Loan Types</option>
                  {loanTypeOptions.map((type) => <option key={type._id} value={type._id}>{type.loanName}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Loan Number</label>
                <input type="text" placeholder="Enter Loan No" value={loanNo} onChange={(e) => setLoanNo(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
              </div>
              <div>
                <label style={labelStyle}>Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
              </div>
            </div>

            <button onClick={handleFilter}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
              <i className="mdi mdi-magnify" /> Filter
            </button>
          </div>

          {/* ── Results ── */}
          {hasFiltered ? (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                  <i className="mdi mdi-table-large" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Loan Records</span>
                <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{loans.length} records</span>
              </div>
              <div style={{ padding: "12px 16px" }}>
                <TableComponent data={loans} columns={columns} />
              </div>
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "48px 24px", textAlign: "center" }}>
              <div style={{ width: 60, height: 60, background: "#E8F5E9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <i className="mdi mdi-filter-outline" style={{ fontSize: 28, color: "#2E7D32" }} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>Apply filters above</div>
              <div style={{ fontSize: 12, color: "#A0AEC0" }}>Select filters and click Filter to view loans</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageLoan;
